<template>
<div class="container" v-if="userReady">
    <core-header
        :fromDashboard="true"
        :user="user"
        :showMode="showMode"
        :isActivePay="user.active_pay"
        @toggleShowMode="toggleShowMode"
    />
    <core-nav-main :user="user"></core-nav-main>

    <md-layout md-align="center" md-row v-if="isInitialProjectLoading" md-flex="100" class="newProjectLoadingBox md-gutter-16" style="text-align: center;">
        <md-progress md-indeterminate></md-progress>
        <div class="sample-project-loading-message">Setting up your first project. This will only take a moment...</div>
    </md-layout>

    <div class="dashboard" v-if="projectsReady">
        <!-- Show Pass next-step callout: after purchase the user holds a pass but
             the "create a project to activate it" link isn't obvious. This spells
             out the next step wherever they land (empty or populated dashboard). -->
        <div v-if="hasUnusedShowPass" style="display:flex;align-items:center;gap:12px;background:#0d2744;color:#fff;border-radius:4px;padding:12px 16px;margin:20px 0 16px;">
            <!-- Monochrome inline SVG, not the emoji: every other mark in the app is a
                 currentColor SVG, and a full-colour glyph was the only one of its kind.
                 Perforation is deliberately lighter than the edge (1.3 vs 1.6) so it reads
                 as a subordinate detail. The same icon is in account.vue - move both. -->
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"
                 style="flex:none;display:block;">
                <path stroke-width="1.6" d="M4 5H20A2 2 0 0 1 22 7V9.5A2.5 2.5 0 0 0 22 14.5V17A2 2 0 0 1 20 19H4A2 2 0 0 1 2 17V14.5A2.5 2.5 0 0 0 2 9.5V7A2 2 0 0 1 4 5Z"/>
                <path stroke-width="1.3" stroke-dasharray="1.4 1.8" d="M15.6 6.4V17.6"/>
            </svg>
            <span style="flex:1;font-size:14px;line-height:1.35;">
                You have {{ showPassCount }} Show Pass{{ showPassCount > 1 ? 'es' : '' }} ready. <strong>Start a new project</strong> to activate {{ showPassCount > 1 ? 'one' : 'it' }} and invite your whole team to edit it.
                <!-- Josh, 16 Oct 2026: until the dashboard picker ships, point to the in-show button. -->
                <span style="display:block;margin-top:4px;opacity:.85;">To use {{ showPassCount > 1 ? 'one' : 'it' }} on an existing project, open that show and choose “Use your Show Pass on this show” from the Project Settings menu.</span>
            </span>
            <button type="button" @click="openCreateProject(true)" class="showpass-activate-btn">Activate Show Pass</button>
        </div>

        <!-- Marketing consent, asked ONCE, after the account exists.
             ⚠ It is here rather than on the signup form because it cannot work
             there: login() is bound to the Google button on BOTH the sign-in and
             register views and calls createUser unconditionally, so a brand-new
             account is routinely created from the SIGN-IN screen where a
             register-screen checkbox is not rendered at all. 46% of accounts are
             created with Google. Every route lands here.
             Gated on `hasUnusedShowPass` being false: that banner is commercial
             and time-sensitive, consent can wait a day, and two stacked callouts
             read as clutter rather than as either message. -->
        <div v-if="!hasUnusedShowPass && needsConsentAnswer" class="consent-banner">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                 aria-hidden="true" class="cb-icon">
                <path d="M3 6.5h18v11H3z"/><path d="m3 7 9 6 9-6"/>
            </svg>
            <span class="cb-copy">
                <strong class="cb-q">Want updates about Cuelist?</strong>
                <span class="cb-sub">Occasional email about new features, nothing else. <strong>Answer either way and we won&rsquo;t ask again</strong> &mdash; you can change it any time in your account settings.</span>
            </span>
            <span class="cb-actions">
                <button type="button" class="cb-btn cb-yes" :disabled="consentSaving" @click="answerConsent(true)">Yes, keep me posted</button>
                <button type="button" class="cb-btn cb-no" :disabled="consentSaving" @click="answerConsent(false)">No thanks</button>
            </span>
        </div>

        <!-- Zero-project state: keep the first-project CTA the old hero carried -->
        <div class="dash-empty" v-if="!hasAnyProjects">
            <button type="button" class="ql-btn ql-btn--primary btn-start-project" @click="openCreateProject()" v-if="user.active_pay">Start your first Project</button>
            <button type="button" class="ql-btn ql-btn--primary" @click="upgradeSubscription" v-else>Upgrade Account</button>
        </div>

        <template v-else>
            <md-layout v-if="newProjectLoading || newDuplicateProjectLoading" md-flex="100" class="newProjectLoadingBox md-gutter-16" style="text-align: center;">
                <md-progress md-indeterminate></md-progress>
                <div class="newProjectLoadingBox-Message">Your request is running in the background. Please be patient while it completes. Avoid closing or refreshing the page.</div>
            </md-layout>

            <div class="dash-columns">
                <div class="dash-main">
                    <!-- The toolbar lives inside the main column, not above both
                         columns: Sort and View control the project list, so they
                         belong on the list's width. Spanning the full page put
                         them against the rail's right edge instead, leaving a
                         ~444px gap between them and the tabs. -->
                    <dashboard-toolbar
                        :tab="effectiveTab"
                        :viewMode="viewMode"
                        :sort="sort"
                        :hasShared="hasSharedProjects"
                        @tabChange="onTabChange"
                        @viewChange="onViewChange"
                        @sortChange="setSort"
                    />
                    <div class="dash-tab-empty" v-if="tabIsEmpty">
                        <p>No projects in this view.</p>
                    </div>
                    <template v-else>
                        <dashboard-section
                            v-if="pinnedDisplay.length"
                            key="pinned"
                            title="Pinned"
                            icon="push_pin"
                            :projects="pinnedDisplay"
                            :viewMode="viewMode"
                            :sort="sort"
                            @sortChange="toggleSort"
                            @projectAccepted="handleProjectAccepted"
                            @projectDeleted="handleProjectDeleted"
                        />
                        <dashboard-section
                            key="recent"
                            title="Recent"
                            icon="schedule"
                            :projects="recentDisplay"
                            :viewMode="viewMode"
                            :sort="sort"
                            :showViewMore="!allProjectsLoaded"
                            :loadingMore="loadingMore"
                            @sortChange="toggleSort"
                            @viewMore="loadMoreProjects"
                            @projectAccepted="handleProjectAccepted"
                            @projectDeleted="handleProjectDeleted"
                        />
                    </template>
                </div>
                <!-- Right rail: the primary action, then things needing attention.
                     Always rendered now (it used to be v-if'd on
                     userPendingProjectsData, which starts false) because it owns
                     the create CTA; the pending panels keep their own guards. -->
                <div class="dash-side">
                    <button
                        type="button"
                        class="ql-btn ql-btn--primary dash-side-cta"
                        @click="openCreateProject()"
                        v-if="user.active_pay"
                    >Start a New Project</button>
                    <button
                        type="button"
                        class="ql-btn ql-btn--primary dash-side-cta"
                        @click="upgradeSubscription"
                        v-else
                    >Upgrade Account</button>
                    <dashboard-pending-projects
                        v-if="userPendingProjectsData"
                        :userPendingProjectsData="userPendingProjectsData"
                        :user="user"
                        @projectAccepted="handleProjectAccepted"
                    />
                    <dashboard-pending-affiliations
                        v-if="userPendingProjectsData"
                        :pendingReady="affiliateInvitesReady"
                        :user="user"
                        @affiliationAccepted="handleAffiliationAccepted"
                    />
                </div>
            </div>
        </template>
    </div>

    <div class="dashboard" v-else>
        <div class="loading-msg" style="display: flex;flex-direction: column;align-items: center;">
            <span class="md-subheader"><svg style="height: 56px; margin-top: 72px;" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><circle fill='#73D5E3' stroke='#73D5E3' stroke-width='15' r='15' cx='40' cy='65'><animate attributeName='cy' calcMode='spline' dur='2' values='65;135;65;' keySplines='.5 0 .5 1;.5 0 .5 1' repeatCount='indefinite' begin='-.4'></animate></circle><circle fill='#73D5E3' stroke='#73D5E3' stroke-width='15' r='15' cx='100' cy='65'><animate attributeName='cy' calcMode='spline' dur='2' values='65;135;65;' keySplines='.5 0 .5 1;.5 0 .5 1' repeatCount='indefinite' begin='-.2'></animate></circle><circle fill='#73D5E3' stroke='#73D5E3' stroke-width='15' r='15' cx='160' cy='65'><animate attributeName='cy' calcMode='spline' dur='2' values='65;135;65;' keySplines='.5 0 .5 1;.5 0 .5 1' repeatCount='indefinite' begin='0'></animate></circle></svg></span>
            <span class="md-subheader">Loading your projects...</span>
        </div>
    </div>

    <create-project ref="createProject" @projectAccepted="handleProjectAccepted"></create-project>
    <upgrade-account ref="upgradeAcct"></upgrade-account>
    <maintenance-notice ref="maintenanceNotice"></maintenance-notice>
    <core-footer></core-footer>
</div>
</template>

<script>
import firebase from 'firebase/app';
import { db } from '@/main';
import repos from '@/data';
import { mapGetters } from 'vuex';
import Vue from 'vue';

import CoreNavMain from '@/components/core/nav/main';
import CoreHeader from '@/components/core/header';
import CoreFooter from '@/components/core/footer';
import DashboardToolbar from '@/components/dashboard/dashboard-toolbar';
import { consentFields, consentPromptSource } from '@/lib/marketingConsent';
import DashboardSection from '@/components/dashboard/dashboard-section';
import DashboardPendingProjects from '@/components/dashboard/pending-projects';
import DashboardPendingAffiliations from '@/components/dashboard/pending-affiliations';
import CreateProject from '@/modals/create-project';
import UpgradeAccount from '@/modals/upgrade-account';
import MaintenanceNotice from '@/modals/maintenance-notice';
import { sortProjects, needsAllProjectsForSort } from '@/lib/projectSort';

const VIEW_KEY = 'dashboard-view-selected';
const TAB_KEY = 'dashboard-tab-selected';
const VALID_TABS = ['my', 'shared', 'all'];

function readStoredTab() {
    const stored = typeof window !== 'undefined' && window.localStorage.getItem(TAB_KEY);
    return VALID_TABS.indexOf(stored) !== -1 ? stored : 'all';
}

// Sort is remembered like the tab and the view mode. It used to live only in
// this component's data, so it reset to "Last Updated" every time you left the
// dashboard (Josh, 23 Sept 2026). Validated on read, so a stale or hand-edited
// value falls back to the default instead of breaking the list.
const SORT_KEY = 'dashboard-sort-selected';
const DEFAULT_SORT = { key: 'date', dir: 'desc' };
function readStoredSort() {
    try {
        const stored = JSON.parse(window.localStorage.getItem(SORT_KEY));
        if (stored && ['name', 'date'].indexOf(stored.key) !== -1 && ['asc', 'desc'].indexOf(stored.dir) !== -1) {
            return { key: stored.key, dir: stored.dir };
        }
    } catch (e) { /* private browsing or bad JSON: use the default */ }
    return { ...DEFAULT_SORT };
}
function storeSort(sort) {
    try { window.localStorage.setItem(SORT_KEY, JSON.stringify(sort)); } catch (e) { /* ignore */ }
}

const DashboardView = {
    name: 'dashboard',
    data() {
        return {
            consentSaving: false,
            // Set the moment an answer is given, so the card leaves immediately
            // rather than waiting for the Firestore write to echo back through
            // the binding. Without it the buttons sit there looking unpressed.
            consentAnswered: false,
            projectsReady: false,
            userReady: false,
            userPendingProjectsData: false,
            affiliateInvitesReady: false,
            theme: 'default',
            currentBatch: 0,
            initialBatchSize: 9,
            batchSize: 8,
            allProjectsLoaded: false,
            loadingMore: false,
            userProjectIds: [],
            isInitialProjectLoading: false,
            pinnedLoaded: false,
            // Redesign UI state. Tab defaults to All Projects on first visit,
            // then remembers the user's choice across visits (localStorage).
            tab: readStoredTab(),
            viewMode: (typeof window !== 'undefined' && window.localStorage.getItem(VIEW_KEY)) || 'grid',
            sort: readStoredSort()
        };
    },
    computed: {
        ...mapGetters([
            'userProjects',
            'pinnedProjects',
            'recentProjects',
            'user',
            'showMode',
            'newProjectLoading',
            'newDuplicateProjectLoading'
        ]),
        showPassCount() {
            return (this.user && this.user.showPasses) || 0;
        },
        // Two gates, and they answer different questions.
        //
        // ⚠ ABSENCE is the trigger, not `false`. An account with no
        // `marketing_consent` field has never been asked; one holding `false`
        // has answered no and must never be asked again. Testing for falsiness
        // would re-ask everyone who declined, every time they load the page.
        //
        // ⛔ AND the account must be NEW (Josh, 9 Sept). Every one of the 7,322
        // existing accounts has no consent record, so absence alone would put
        // this banner in front of the entire customer base on their next login.
        // isPromptableAccount reads Firebase Auth's creationTime — `users/{uid}`
        // has no created-at to compare against — and fails CLOSED, so a missing
        // or unparseable timestamp means "do not ask".
        // Which surface is asking, or null for "do not ask". Two reasons now:
        // a new account, or someone who holds (or held) an institutional seat.
        // The rule itself lives in marketingConsent.js so this view and the
        // write below cannot drift apart about who was asked and why.
        consentSource() {
            if (this.consentAnswered) return null;
            if (!this.user || !this.user.uid) return null;
            // ⚠ ABSENCE IS THE TRIGGER. `false` means they answered no and is
            // respected forever — which is what makes this once-only for the
            // affiliate branch exactly as it is for new signups.
            if (this.user.marketing_consent !== undefined) return null;
            return consentPromptSource(firebase.auth().currentUser, this.user);
        },
        needsConsentAnswer() {
            return this.consentSource !== null;
        },
        // Any user holding unused Show Pass credit(s) — subscriber or not —
        // drives the next-step callout + its "Activate Show Pass" button. A
        // subscriber can buy a pass (producer-paid show) on top of their
        // subscription; the banner button is the deliberate consume entry
        // point, separate from the normal "Start a New Project" create.
        hasUnusedShowPass() {
            return !!(this.user && this.showPassCount > 0);
        },
        hasAnyProjects() {
            return this.userProjects.length > 0;
        },
        hasSharedProjects() {
            return this.userProjects.some(p => p.ownerUID !== this.user.uid);
        },
        // The saved tab preference is honored, but if it's "Shared with Me"
        // while the user currently has no shared projects (that tab is hidden),
        // fall back to All without discarding the saved preference.
        effectiveTab() {
            if (this.tab === 'shared' && !this.hasSharedProjects) return 'all';
            return this.tab;
        },
        tabIsEmpty() {
            return this.filterByTab(this.userProjects).length === 0;
        },
        pinnedDisplay() {
            return sortProjects(this.filterByTab(this.pinnedProjects), this.sort.key, this.sort.dir);
        },
        recentDisplay() {
            return sortProjects(this.filterByTab(this.recentProjects), this.sort.key, this.sort.dir);
        }
    },
    watch: {
        showMode: {
            immediate: true,
            handler(newVal) {
                this.theme = newVal ? 'show-mode' : 'default';
                Vue.material.setCurrentTheme(this.theme);
            }
        }
    },
    methods: {
        async answerConsent(granted) {
            const source = this.consentSource || 'first_run';
            // Optimistic: the card goes on click. It is a one-time prompt, so
            // leaving it on screen while a write settles reads as broken — and
            // if the write fails, `marketing_consent` stays absent and they are
            // asked again next load, which is the correct fallback.
            this.consentSaving = true;
            this.consentAnswered = true;
            try {
                // Recorded against the surface that actually asked, so the
                // affiliate prompt's yield can be measured on its own rather
                // than being folded into the new-signup banner's numbers.
                // Captured before the optimistic flag above clears it.
                await repos.users.update(this.user.uid,
                    consentFields(granted, source));
            } catch (e) {
                console.error('marketing consent save failed:', e.message);
                this.consentAnswered = false;
            } finally {
                this.consentSaving = false;
            }
        },
        filterByTab(list) {
            if (this.effectiveTab === 'my') return list.filter(p => p.ownerUID === this.user.uid);
            if (this.effectiveTab === 'shared') return list.filter(p => p.ownerUID !== this.user.uid);
            return list; // all
        },
        onTabChange(tab) {
            this.tab = tab;
            try { window.localStorage.setItem(TAB_KEY, tab); } catch (e) { /* ignore */ }
        },
        onViewChange(mode) {
            this.viewMode = mode;
            try { window.localStorage.setItem(VIEW_KEY, mode); } catch (e) { /* ignore */ }
        },
        setSort({ key, dir }) {
            this.sort = { key, dir };
            storeSort(this.sort);
            this.loadAllForSort();
        },
        toggleSort(key) {
            if (this.sort.key === key) {
                this.sort = { key, dir: this.sort.dir === 'asc' ? 'desc' : 'asc' };
            } else {
                this.sort = { key, dir: key === 'name' ? 'asc' : 'desc' };
            }
            storeSort(this.sort);
            this.loadAllForSort();
        },
        // Batches load newest first, so a name sort (or oldest first) on a
        // partial load leaves out the shows in unloaded batches (Josh, 21 Oct
        // 2026). Load the next batch; loadMoreProjects calls back in here when
        // it lands, until every batch is in. The View more button shows the
        // existing loading state meanwhile. Waits for the first batch, which
        // is what fills userProjectIds.
        loadAllForSort() {
            if (this.currentBatch === 0) return;
            if (!needsAllProjectsForSort(this.sort, this.allProjectsLoaded)) return;
            this.loadMoreProjects();
        },
        toggleShowMode() {
            this.$store.dispatch('updateShowMode', !this.showMode);
            this.theme = !this.showMode ? 'show-mode' : 'default';
            Vue.material.setCurrentTheme(this.theme);
        },
        openCreateProject(spendPass = false) {
            this.$refs.createProject.open(spendPass);
        },
        upgradeSubscription() {
            this.$refs.upgradeAcct.open();
        },
        // Checkout refused the student price (window over; functions checkoutRefusal) and sent the browser back
        // here with ?student_rate=ended: reopen the student view, whose banner says why. Once, then drop the flag.
        reopenRefusedStudentCheckout() {
            const query = (this.$route && this.$route.query) || {};
            if (query.student_rate !== 'ended') return;
            // The dashboard renders on userReady and the dialog sets itself up a moment later; opening first
            // throws inside vue-material, so try a few times.
            const tryOpen = (left) => {
                try {
                    this.$refs.upgradeAcct.open({ mode: 'student' });
                } catch (e) {
                    if (left > 0) setTimeout(() => tryOpen(left - 1), 300);
                }
            };
            this.$nextTick(() => tryOpen(10));
            const rest = Object.assign({}, query);
            delete rest.student_rate;
            // This vue-router returns nothing from replace() without callbacks.
            this.$router.replace({ query: rest }, () => {}, () => {});
        },
        async loadMoreProjects() {
            if (this.loadingMore || this.allProjectsLoaded) return;
            this.loadingMore = true;

            const startIndex = this.currentBatch === 0 ? 0 : this.initialBatchSize + ((this.currentBatch - 1) * this.batchSize);
            const endIndex = this.currentBatch === 0 ? this.initialBatchSize : startIndex + this.batchSize;
            const batchIds = this.userProjectIds.slice(startIndex, endIndex);

            if (batchIds.length === 0) {
                this.allProjectsLoaded = true;
                this.loadingMore = false;
                return;
            }
            const projectQuery = db.collection('projects').where('id', 'in', batchIds);
            this.$store.dispatch('setProjectsRef', {
                ref: projectQuery,
                callback: () => {
                    this.currentBatch++;
                    this.loadingMore = false;
                    this.projectsReady = true;
                    if (endIndex >= this.userProjectIds.length) {
                        this.allProjectsLoaded = true;
                    }
                    // Once the first page is in (projects binding established),
                    // eager-load any pinned projects that sort past the window
                    // so the Pinned section is always complete.
                    this.ensurePinnedLoaded();
                    // A remembered name or oldest-first sort needs every batch, not just the first.
                    this.loadAllForSort();
                }
            });
        },
        // Pinned projects can sort far down the date-ordered list and never
        // enter the paginated window. All pin flags live in user.projects at
        // load, so fetch the pinned ids directly (chunked to Firestore's
        // in-query limit of 10) and merge them into the projects store.
        ensurePinnedLoaded() {
            if (this.pinnedLoaded) return;
            this.pinnedLoaded = true;
            const projectsMap = this.user.projects || {};
            const loaded = new Set(this.userProjects.map(p => p.id));
            const pinnedIds = Object.keys(projectsMap)
                .filter(id => projectsMap[id] && projectsMap[id].pinned && !loaded.has(id));
            if (!pinnedIds.length) return;

            for (let i = 0; i < pinnedIds.length; i += 10) {
                const chunk = pinnedIds.slice(i, i + 10);
                const query = db.collection('projects').where('id', 'in', chunk);
                this.$store.dispatch('setProjectsRef', { ref: query, callback: () => {} });
            }
        },
        handleProjectAccepted(projectId) {
            // A just-created or just-accepted project is added to user.projects
            // (live activeUser binding — reactive), but its DATA was never
            // fetched into state.projects: the project-list bindings are built
            // once at load from the original id list, so a project outside that
            // set is missing from userProjects until re-bound. Rather than a
            // jarring full-page reload (which also races the write's server
            // propagation and could still show nothing), fetch just this project
            // into the store so it surfaces immediately — mirroring the reactive
            // drop in handleProjectDeleted. Fall back to a reload only if we
            // somehow weren't told which project.
            if (!projectId) {
                window.location.reload(true);
                return;
            }
            const query = db.collection('projects').where('id', 'in', [projectId]);
            // The fetch above is a one-off snapshot, and the updateProject trigger
            // stamps the project's lastChangedDate a moment AFTER the write that
            // created or joined it. So the snapshot carries the OLD date and a
            // date sort buried the project until the next visit (Josh, 23 Sept
            // 2026). It was just created, duplicated or joined: "now" is what the
            // server is about to write, so show that.
            this.$store.dispatch('setProjectsRef', {
                ref: query,
                callback: () => {
                    const projects = this.$store.state.projects || {};
                    const fetched = projects[projectId];
                    const now = new Date().toISOString();
                    if (fetched && (!fetched.lastChangedDate || fetched.lastChangedDate < now)) {
                        this.$store.commit('SET_PROJECTS', {
                            ...projects,
                            [projectId]: { ...fetched, lastChangedDate: now }
                        });
                    }
                }
            });
            // Keep pagination bookkeeping in sync so loadMoreProjects math holds.
            if (!this.userProjectIds.includes(projectId)) {
                this.userProjectIds.push(projectId);
            }
        },
        handleAffiliationAccepted() {
            // Accepting affiliates the user; the seat cascade runs server-side
            // across two docs. Reload so the account page reflects the new
            // subscription/affiliation state without stale reads.
            window.location.reload(true);
        },
        handleProjectDeleted() {
            // Delete removes the project from user.projects; the live activeUser
            // binding drops it from userProjects reactively — no reload needed.
        }
    },
    components: {
        'core-nav-main': CoreNavMain,
        'core-header': CoreHeader,
        'core-footer': CoreFooter,
        'dashboard-toolbar': DashboardToolbar,
        'dashboard-section': DashboardSection,
        'dashboard-pending-projects': DashboardPendingProjects,
        'dashboard-pending-affiliations': DashboardPendingAffiliations,
        'create-project': CreateProject,
        'upgrade-account': UpgradeAccount,
        'maintenance-notice': MaintenanceNotice
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            const userUID = firebase.auth().currentUser.uid;
            const userEmail = firebase.auth().currentUser.email;
            vm.$store.dispatch('setActiveUser', {
                ref: db.collection('users').doc(userUID),
                callback: () => {
                    vm.userReady = true;
                    vm.theme = vm.showMode ? 'show-mode' : 'default';
                    Vue.material.setCurrentTheme(vm.theme);
                    vm.reopenRefusedStudentCheckout();

                    const projects = Object.entries(vm.user.projects)
                        .map(([projectId, projectData]) => ({ id: projectId, ...projectData }))
                        .filter(project => project.lastChangedDate)
                        .sort((a, b) => new Date(b.lastChangedDate) - new Date(a.lastChangedDate))
                        .reduce((acc, project) => {
                            const { id, ...projectData } = project;
                            acc[id] = projectData;
                            return acc;
                        }, {});

                    vm.userProjectIds = Object.keys(projects);

                    if (vm.userProjectIds.length === 0) {
                        // Brand-new accounts have their sample project seeded
                        // asynchronously; poll (via reload) a capped number of
                        // times, then show the empty dashboard.
                        const ATTEMPTS_KEY = 'cuelist_first_project_attempts';
                        const MAX_ATTEMPTS = 4;
                        const attempts = parseInt(window.sessionStorage.getItem(ATTEMPTS_KEY) || '0', 10);
                        if (attempts < MAX_ATTEMPTS) {
                            window.sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));
                            vm.isInitialProjectLoading = true;
                            setTimeout(() => {
                                vm.isInitialProjectLoading = false;
                                window.location.reload(true);
                            }, 8000);
                        } else {
                            vm.isInitialProjectLoading = false;
                            vm.projectsReady = true;
                        }
                    } else {
                        window.sessionStorage.removeItem('cuelist_first_project_attempts');
                        vm.loadMoreProjects();
                    }
                }
            });

            vm.$store.dispatch('setInvitationsRef', {
                ref: db.collection('invitations').where('email', '==', userEmail.toLowerCase()),
                callback: () => {
                    vm.userPendingProjectsData = true;
                }
            });

            // Email-scoped live binding for affiliate invites — surfaces an
            // invite (incl. one sent before this user had an account) with no
            // refresh, reconciled purely by email match.
            vm.$store.dispatch('setAffiliateInvitationsRef', {
                ref: db.collection('affiliateInvitations').where('email', '==', userEmail.toLowerCase()),
                callback: () => {
                    vm.affiliateInvitesReady = true;
                }
            });
        });
    }
};
export default DashboardView;
</script>

<style lang="scss">
// Marketing consent banner.
//
// ⚠ DELIBERATELY PROMINENT, and the earlier lighter treatment was wrong. It was
// styled as a quiet aside on the reasoning that consent is less urgent than a
// Show Pass. Josh, testing it: "I'm honestly not sure it is that obvious... we
// might just have users who live with that thing on top of their dashboard for
// weeks at a time."
//
// The reframe: annoyance is a function of REPETITION, not prominence. This is
// answered once and then never shown again, so being in the way costs almost
// nothing -- whereas a polite banner nobody clicks becomes a permanent fixture
// on every dashboard, which is both more annoying and captures no consent. It
// therefore borrows the Show Pass banner's filled treatment, which the app
// already uses for "the dashboard is telling you something".
//
// The copy carries the other half: it has to answer the question Josh actually
// had -- will this go away if I click it -- because if that is unclear, ignoring
// it is the rational choice.
//
// ⚠ It says "answer either way and we won't ask again", NOT "we'll only ask
// once". The second version makes a claim about history that is false for
// anyone who already saw the checkbox on the email signup form and skipped it:
// from their side this is the second ask, and a banner insisting otherwise is
// simply wrong. The forward-looking phrasing is true on every path -- email,
// Google, ignored, declined -- and it is the stronger reassurance anyway,
// because what someone wants to know is what happens AFTER they click.
.consent-banner {
    display: flex;
    align-items: center;
    gap: 16px;
    // Teal, not the Show Pass banner's navy (Josh, 8 Sept). Same weight, and it
    // keeps the two messages visually distinct: navy is the commercial one,
    // teal is Cuelist asking you something. Dark navy ink on the accent rather
    // than white -- white on #73d5e3 fails contrast badly, and the app already
    // pairs this exact background with #0b2a31 text on the login CTA.
    background: #73d5e3;
    color: #0b2a31;
    border-radius: 4px;
    padding: 18px 20px;
    margin: 20px 0 16px;

    .cb-icon { flex: none; display: block; opacity: .75; }
    .cb-copy { flex: 1; min-width: 0; }
    .cb-q { display: block; font-size: 15.5px; font-weight: 700; line-height: 1.3; }
    .cb-sub {
        display: block; font-size: 13px; line-height: 1.45;
        color: rgba(11,42,49,.78); margin-top: 4px;
        strong { color: #0b2a31; font-weight: 700; }
    }
    .cb-actions { flex: none; display: flex; gap: 10px; flex-wrap: wrap; }
    .cb-btn {
        font-size: 13px; font-weight: 700; padding: 10px 18px;
        border-radius: 4px; border: 1px solid transparent; cursor: pointer;
        white-space: nowrap; transition: background .15s, box-shadow .15s;
        &:disabled { opacity: .55; cursor: progress; }
    }
    .cb-yes {
        background: #0d2744; color: #fff;
        box-shadow: 0 1px 3px rgba(11,42,49,.22);
        &:hover:not(:disabled) { background: #16375f; box-shadow: 0 3px 8px rgba(11,42,49,.28); }
    }
    .cb-no {
        background: transparent; color: #0b2a31;
        border-color: rgba(11,42,49,.34);
        &:hover:not(:disabled) { background: rgba(255,255,255,.35); border-color: rgba(11,42,49,.6); }
    }
}
@media (max-width: 760px) {
    .consent-banner {
        align-items: flex-start; flex-wrap: wrap;
        .cb-actions { width: 100%; margin-top: 4px; }
        .cb-btn { flex: 1; }
    }
}

// Show Pass activation button in the dashboard callout banner. Hover lift +
// shadow mirrors the site's raised-button feel.
.showpass-activate-btn {
    background: #fff;
    color: #0d2744;
    border: none;
    padding: 9px 18px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    cursor: pointer;
    white-space: nowrap;
    transition: box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease;
    &:hover {
        background: #eaf2fb;
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
        transform: translateY(-1px);
    }
    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    }
}

.dashboard {
    min-height: calc(100% - 64px);
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 16px;
    margin-bottom: 30px;

    .dash-empty {
        margin-top: 48px;
        text-align: center;
        /* Colour comes from the design-system token (navy in light, teal in dark);
           the old teal !important override is gone. */
    }
    .dash-columns {
        display: flex;
        gap: 24px;
        align-items: flex-start;
        // Carries the spacing the toolbar's top margin used to provide, now
        // that the toolbar sits inside the left column.
        margin-top: 16px;
    }
    .dash-main { flex: 1 1 auto; min-width: 0; }
    // No top offset any more. The panel used to be dropped 30px so its top lined
    // up with the underline beneath the first section's title; now that the
    // create CTA heads this column, the rail top-aligns with the main column so
    // the button sits level with the first section header instead.
    // Flex column with a gap, not per-child margins. The CTA used to carry a
    // margin-bottom that was doing all the spacing in this rail, so the moment a
    // second panel appeared below the first the two sat flush against each other
    // (Josh, 8 Sept: "the two cards are bumping into each other"). One gap spaces
    // every child, including whatever gets added here next.
    .dash-side {
        flex: 0 0 420px; max-width: 420px;
        display: flex; flex-direction: column; gap: 14px;
    }
    .dash-side-cta { display: block; width: 100%; }
    .dash-tab-empty {
        margin-top: 32px;
        color: #888;
        text-align: center;
    }
}
.sample-project-loading-message {
    margin-top: 50px;
    font-size: 20px;
    font-weight: 700;
}
@media (max-width: 900px) {
    .dashboard .dash-columns { flex-direction: column; }
    // Hoist the rail above the project list when the columns stack, so the
    // create CTA it now heads doesn't end up buried at the foot of the page.
    .dashboard .dash-side {
        flex-basis: auto;
        max-width: none;
        width: 100%;
        order: -1;
        margin-top: 0;
        margin-bottom: 24px;
    }
}
</style>


// WEBPACK FOOTER //
// dashboard.vue?ea314b16